/**
 * Маленькая карта «ты → укрытие» для баннера городской тревоги и SOS на
 * главной странице (City Resilience Hack, 24.09.2026) — по образцу
 * directory-map.ts (тот же ленивый maplibre-gl, но без кластеров: тут
 * максимум пара точек и линия маршрута).
 */

import type { LonLat } from '@app/geo'
import type maplibregl from 'maplibre-gl'
import { importChunk } from '../lazy-retry'
import type { ShelterRoute } from './shelter-route'

export interface AlertMapState {
  readonly you: LonLat | null
  readonly shelter: LonLat | null
  readonly route: ShelterRoute | null
}

export interface AlertMap {
  setState(state: AlertMapState): void
  destroy(): void
}

const SRC_YOU = 'alert-you'
const SRC_SHELTER = 'alert-shelter'
const SRC_LINE = 'alert-line'

function point(coord: LonLat | null): unknown {
  return {
    type: 'FeatureCollection',
    features: coord
      ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: coord }, properties: {} }]
      : [],
  }
}

function line(route: ShelterRoute | null): unknown {
  return {
    type: 'FeatureCollection',
    features: route
      ? [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: route.coordinates },
            properties: { straight: route.straight },
          },
        ]
      : [],
  }
}

function boundsOf(coords: readonly LonLat[]): [[number, number], [number, number]] | null {
  if (coords.length === 0) return null
  let minLng = Number.POSITIVE_INFINITY
  let maxLng = Number.NEGATIVE_INFINITY
  let minLat = Number.POSITIVE_INFINITY
  let maxLat = Number.NEGATIVE_INFINITY
  for (const [lng, lat] of coords) {
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

export async function createAlertMap(
  container: HTMLElement,
  initial: AlertMapState,
): Promise<AlertMap> {
  const lib = (await importChunk(() => import('maplibre-gl'))).default
  await importChunk(() => import('maplibre-gl/dist/maplibre-gl.css'))

  const centerSrc = initial.you ?? initial.shelter ?? ([24.7536, 59.437] as const)
  const center: [number, number] = [centerSrc[0], centerSrc[1]]
  const map = new lib.Map({
    container,
    style: 'https://tiles.openfreemap.org/styles/dark',
    center,
    zoom: 13,
    attributionControl: { compact: true },
  })
  map.addControl(new lib.NavigationControl({ showCompass: false }), 'top-right')

  const apply = (s: AlertMapState): void => {
    const srcYou = map.getSource(SRC_YOU) as maplibregl.GeoJSONSource | undefined
    const srcShelter = map.getSource(SRC_SHELTER) as maplibregl.GeoJSONSource | undefined
    const srcLine = map.getSource(SRC_LINE) as maplibregl.GeoJSONSource | undefined
    srcYou?.setData(point(s.you) as never)
    srcShelter?.setData(point(s.shelter) as never)
    srcLine?.setData(line(s.route) as never)
    const coords = [s.you, s.shelter].filter((c): c is LonLat => c !== null)
    const b = boundsOf(coords)
    if (b) map.fitBounds(b, { padding: 60, maxZoom: 16, duration: 300 })
  }

  let pending = initial
  map.on('load', () => {
    map.addSource(SRC_LINE, { type: 'geojson', data: line(initial.route) as never })
    map.addLayer({
      id: 'alert-line-layer',
      type: 'line',
      source: SRC_LINE,
      paint: {
        'line-color': '#22d3a6',
        'line-width': 4,
        'line-dasharray': ['case', ['get', 'straight'], ['literal', [2, 2]], ['literal', [1, 0]]],
      },
    })
    map.addSource(SRC_SHELTER, { type: 'geojson', data: point(initial.shelter) as never })
    map.addLayer({
      id: 'alert-shelter-layer',
      type: 'circle',
      source: SRC_SHELTER,
      paint: {
        'circle-radius': 9,
        'circle-color': '#ff6a3d',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    })
    map.addSource(SRC_YOU, { type: 'geojson', data: point(initial.you) as never })
    map.addLayer({
      id: 'alert-you-layer',
      type: 'circle',
      source: SRC_YOU,
      paint: {
        'circle-radius': 7,
        'circle-color': '#2b5cff',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    })
    apply(pending)
  })

  return {
    setState: (s) => {
      pending = s
      if (map.getSource(SRC_YOU)) apply(s)
    },
    destroy: () => map.remove(),
  }
}
